import { useEffect, useRef, useState } from "react";
import { ChatCircleDots, X, PaperPlaneRight, DotsSixVertical } from "@phosphor-icons/react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useCustomerAuth, customerApi } from "../lib/customerAuth";
import { api } from "../lib/api";
import { openChatSocket } from "../lib/chatSocket";
import { useDraggable } from "../lib/useDraggable";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

/**
 * Sprint H · Batch 6 — Widget de chat flotante global ARRASTRABLE.
 *
 *  - user (admin/colab/empleado): link a /chats.
 *  - CustomerAccount logueado: chat directo con el último taller.
 *  - Anónimo en /reservar/:slug: mini-form (nombre + email/tel) → POST
 *    /api/chats/anon-start → conecta WS como customer → chat en vivo.
 *
 * El widget se puede arrastrar libremente — la posición persiste en
 * localStorage. Cada variante usa su propio storageKey.
 */
const HIDDEN_ROUTES = ["/chats"];

export function FloatingChatWidget() {
  const { user } = useAuth() || {};
  const { account } = useCustomerAuth() || {};
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const hidden = HIDDEN_ROUTES.some((r) => location.pathname.startsWith(r))
    || (location.pathname.startsWith("/mi-cuenta") && location.search.includes("tab=chats"));
  if (hidden) return null;

  // Taller: link a bandeja
  if (user) {
    return <ShopFloatingChat />;
  }

  // Cliente final logueado → chat embebido con su último taller
  if (account) return <CustomerFloatingChat open={open} setOpen={setOpen} />;

  // Anónimo en /reservar/:slug → permitir iniciar chat con identificación mínima.
  // IMPORTANTE: aunque tras anon-start exista cookie de cliente guest, mantenemos
  // AnonymousFloatingChat mientras el visitante siga en /reservar/* para no
  // desmontar la conversación en curso.
  // Sprint UNIFY · #21: en /reservar/* el FAB queda OCULTO; el `PublicChatLauncher`
  // gestiona la apertura mediante un evento global. AnonymousFloatingChat sigue
  // viva (montada) para no perder la sesión, pero sin su propio botón.
  if (location.pathname.startsWith("/reservar/")) {
    return <AnonymousFloatingChat slug={location.pathname.split("/")[2]} controlledByLauncher />;
  }

  return null;
}

// ---------------------------------------------------------------------------
// Grip handle reutilizable — pequeña "manija" arrastrable junto al botón
// ---------------------------------------------------------------------------
function DragGripBadge({ handleProps, testId }) {
  return (
    <button
      {...handleProps}
      type="button"
      aria-label="Mover chat"
      title="Arrastra para mover"
      data-testid={testId}
      className="w-7 h-7 grid place-items-center bg-[#1A1A1A]/80 hover:bg-[#1A1A1A] text-white/70 hover:text-white rounded-full shadow-lg backdrop-blur"
      onClick={(e) => e.preventDefault()}
    >
      <DotsSixVertical size={14} weight="bold" />
    </button>
  );
}

// ---------------------------------------------------------------------------
// Taller (admin/colab/empleado) — link a /chats, draggable
// ---------------------------------------------------------------------------
function ShopFloatingChat() {
  const { ref, dragHandleProps, style } = useDraggable({ storageKey: "ag_chat_pos_shop" });
  return (
    <div ref={ref} style={style} className="flex items-center gap-1" data-testid="floating-chat-shop-wrap">
      <DragGripBadge handleProps={dragHandleProps} testId="floating-chat-shop-grip" />
      <Link
        to="/chats"
        className="group inline-flex items-center gap-2 bg-[var(--ag-gold)] hover:bg-[#FFCD3C] text-white px-4 py-3 rounded-full shadow-2xl transition-all"
        data-testid="floating-chat-shop"
        title="Bandeja de mensajes del taller"
      >
        <ChatCircleDots size={20} weight="fill" />
        <span className="hidden sm:inline text-sm font-semibold">Mensajes</span>
      </Link>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cliente final logueado
// ---------------------------------------------------------------------------
function CustomerFloatingChat({ open, setOpen }) {
  const { ref, dragHandleProps, style } = useDraggable({ storageKey: "ag_chat_pos_customer" });
  const [orgs, setOrgs] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [wsOpen, setWsOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const socketRef = useRef(null);
  const scrollerRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    customerApi.get("/customer/organizations").then(({ data }) => {
      setOrgs(data || []);
      if (data?.length && !selectedOrgId) setSelectedOrgId(data[0].id);
    }).catch(()=>{});
  // eslint-disable-next-line
  }, [open]);

  useEffect(() => {
    if (!selectedOrgId || !open) return;
    customerApi.get(`/chats/history-as-customer?organization_id=${selectedOrgId}`)
      .then(({ data }) => { setMessages(data || []); setTimeout(scrollToBottom, 50); })
      .catch(()=>{});
    if (socketRef.current) socketRef.current.close();
    const conn = openChatSocket({
      kind: "customer", organizationId: selectedOrgId,
      onOpen: () => setWsOpen(true),
      onClose: () => setWsOpen(false),
      onMessage: (msg) => {
        if ((msg?.type === "message" || msg?.type === "ack") && msg.data) {
          setMessages((arr) => arr.some((m) => m.id === msg.data.id) ? arr : [...arr, msg.data]);
          scrollToBottom();
          if (!open && msg.type === "message" && msg.data.sender_type === "organization") {
            setUnread((u) => u + 1);
          }
        }
      },
    });
    socketRef.current = conn;
    return () => { conn.close(); socketRef.current = null; };
  // eslint-disable-next-line
  }, [selectedOrgId, open]);

  useEffect(() => {
    customerApi.get("/chats/unread-count-as-customer").then(({ data }) => {
      setUnread(data?.unread || 0);
    }).catch(()=>{});
  }, []);

  const scrollToBottom = () => {
    setTimeout(() => scrollerRef.current?.scrollTo({ top: scrollerRef.current.scrollHeight, behavior: "smooth" }), 60);
  };

  const send = (e) => {
    e?.preventDefault?.();
    const body = text.trim();
    if (!body || !selectedOrgId) return;
    const conn = socketRef.current;
    if (conn && conn.isOpen()) {
      conn.send({ message_text: body, client_id: `c-${Date.now()}` });
      setText("");
    }
  };

  if (!open) {
    return (
      <div ref={ref} style={style} className="flex items-center gap-1" data-testid="floating-chat-customer-wrap">
        <DragGripBadge handleProps={dragHandleProps} testId="floating-chat-customer-grip" />
        <button
          onClick={() => { setOpen(true); setUnread(0); }}
          className="relative group inline-flex items-center gap-2 bg-[var(--ag-gold)] hover:bg-[#FFCD3C] text-white px-4 py-3 rounded-full shadow-2xl transition-all"
          data-testid="floating-chat-customer-bubble"
          aria-label="Abrir chat"
        >
          <ChatCircleDots size={20} weight="fill" />
          <span className="hidden sm:inline text-sm font-semibold">Chat con tu taller</span>
          {unread > 0 && (
            <span className="absolute -top-1 -right-1 bg-[#FFB703] text-black text-[10px] font-bold w-5 h-5 grid place-items-center rounded-full" data-testid="floating-chat-unread">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </div>
    );
  }

  const selected = orgs.find((o) => o.id === selectedOrgId);
  return (
    <div ref={ref} style={style} className="w-[calc(100vw-2rem)] sm:w-[380px] h-[480px] bg-white border border-gray-200 rounded-md shadow-2xl flex flex-col" data-testid="floating-chat-customer-panel">
      <div {...dragHandleProps} className="px-3 py-2 border-b border-gray-200 flex items-center justify-between bg-[var(--ag-gold)] text-white rounded-t-md" data-testid="floating-chat-customer-dragbar">
        <DotsSixVertical size={14} weight="bold" className="opacity-70" />
        <div className="min-w-0 flex-1 ml-1">
          <div className="text-sm font-semibold truncate">{selected?.name || "Chat"}</div>
          <div className={`text-[9px] uppercase tracking-wider ${wsOpen ? "text-emerald-300" : "text-blue-200"}`}>
            <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1 ${wsOpen ? "bg-emerald-300" : "bg-blue-300"}`} />
            {wsOpen ? "En vivo" : "Conectando…"}
          </div>
        </div>
        <button onClick={(e) => { e.stopPropagation(); setOpen(false); }}
          onMouseDown={(e)=>e.stopPropagation()} onTouchStart={(e)=>e.stopPropagation()}
          className="p-1.5 hover:bg-white/10 rounded" aria-label="Cerrar" data-testid="floating-chat-close">
          <X size={14} weight="bold" />
        </button>
      </div>
      {orgs.length > 1 && (
        <div className="px-2 py-1.5 border-b border-gray-200 flex gap-1 overflow-x-auto" data-testid="floating-chat-org-picker">
          {orgs.map((o) => (
            <button key={o.id} onClick={()=>setSelectedOrgId(o.id)}
              className={`shrink-0 text-[10px] uppercase tracking-wider px-2 py-1 rounded-sm border ${selectedOrgId === o.id ? "bg-[var(--ag-gold)] text-white border-transparent" : "border-gray-200 text-gray-600"}`}>
              {o.name}
            </button>
          ))}
        </div>
      )}
      <div ref={scrollerRef} className="flex-1 overflow-y-auto p-3 space-y-1.5 bg-gray-50" data-testid="floating-chat-messages">
        {messages.length === 0 && (
          <div className="text-center text-xs text-[var(--ag-text-muted)] py-8">Sin mensajes aún. Escribe abajo.</div>
        )}
        {messages.map((m) => {
          const mine = m.sender_type === "customer";
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] rounded-md px-2.5 py-1.5 text-sm ${mine ? "bg-[var(--ag-gold)] text-white" : "bg-white border border-gray-200 text-[#1A1A1A]"}`}>
                {m.message_text}
              </div>
            </div>
          );
        })}
      </div>
      <form onSubmit={send} className="p-2 border-t border-gray-200 flex gap-1.5">
        <Input value={text} onChange={(e)=>setText(e.target.value)} placeholder="Escribe…" className="rounded-sm h-9" data-testid="floating-chat-input" />
        <Button type="submit" disabled={!text.trim()} className="bg-[var(--ag-gold)] hover:bg-[#FFCD3C] rounded-sm h-9 px-3" data-testid="floating-chat-send">
          <PaperPlaneRight size={14} weight="fill" />
        </Button>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Visitante anónimo en /reservar/:slug → form mínimo + chat en vivo
// Sprint UNIFY · #21: cuando `controlledByLauncher` es true, el FAB no se
// renderiza; el componente escucha el evento global `ag:open-human-chat`
// para abrirse desde el `PublicChatLauncher`.
// ---------------------------------------------------------------------------
function AnonymousFloatingChat({ slug, controlledByLauncher = false }) {
  const { ref, dragHandleProps, style } = useDraggable({ storageKey: "ag_chat_pos_anon" });
  const [open, setOpen] = useState(false);
  const [stage, setStage] = useState("form"); // form | chatting
  const [orgId, setOrgId] = useState(null);
  const [orgName, setOrgName] = useState("");
  const [name, setName] = useState("");
  const [contact, setContact] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [wsOpen, setWsOpen] = useState(false);
  const socketRef = useRef(null);
  const scrollerRef = useRef(null);

  // Resolver org_id desde slug (sólo cuando abrimos)
  useEffect(() => {
    if (!open || orgId) return;
    api.get(`/public/org/${slug}`).then(({ data }) => {
      setOrgId(data?.org?.id || null);
      setOrgName(data?.org?.name || "");
    }).catch(() => setErr("No se ha podido cargar el centro"));
  }, [open, slug, orgId]);

  const start = async (e) => {
    e?.preventDefault?.();
    setErr("");
    if (!name.trim() || !contact.trim() || !orgId) {
      setErr("Indica tu nombre y un email o teléfono");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/chats/anon-start", {
        organization_id: orgId,
        name: name.trim(),
        contact: contact.trim(),
      });
      if (data?.ok) {
        // Cambiar a stage chatting ANTES de abrir WS — así el panel
        // queda renderizado y la cookie httpOnly tiene tiempo de propagarse
        // en el siguiente tick de event loop antes del handshake WS.
        setStage("chatting");
        setTimeout(() => openWS(data.organization_id), 250);
      } else {
        setErr("No se pudo iniciar el chat");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setErr(typeof detail === "string" ? detail : "No se pudo iniciar el chat");
    } finally {
      setBusy(false);
    }
  };

  const openWS = (organizationId) => {
    if (socketRef.current) socketRef.current.close();
    // Cargar histórico mínimo
    customerApi.get(`/chats/history-as-customer?organization_id=${organizationId}`)
      .then(({ data }) => { setMessages(data || []); setTimeout(scrollToBottom, 50); })
      .catch(()=>{});
    const conn = openChatSocket({
      kind: "customer", organizationId,
      onOpen: () => setWsOpen(true),
      onClose: () => setWsOpen(false),
      onMessage: (msg) => {
        if ((msg?.type === "message" || msg?.type === "ack") && msg.data) {
          setMessages((arr) => arr.some((m) => m.id === msg.data.id) ? arr : [...arr, msg.data]);
          scrollToBottom();
        }
      },
    });
    socketRef.current = conn;
  };

  const scrollToBottom = () => {
    setTimeout(() => scrollerRef.current?.scrollTo({ top: scrollerRef.current.scrollHeight, behavior: "smooth" }), 60);
  };

  const send = (e) => {
    e?.preventDefault?.();
    const body = text.trim();
    if (!body) return;
    const conn = socketRef.current;
    if (conn && conn.isOpen()) {
      conn.send({ message_text: body, client_id: `c-${Date.now()}` });
      setText("");
    }
  };

  useEffect(() => () => { socketRef.current?.close(); }, []);

  // Sprint UNIFY · #21: escuchar evento global para abrir el chat humano
  // desde el PublicChatLauncher (un solo FAB que ofrece IA vs humano).
  useEffect(() => {
    if (!controlledByLauncher) return;
    const handler = () => setOpen(true);
    window.addEventListener("ag:open-human-chat", handler);
    return () => window.removeEventListener("ag:open-human-chat", handler);
  }, [controlledByLauncher]);

  if (!open) {
    // En modo launcher no renderizamos FAB propio — el launcher decide.
    if (controlledByLauncher) return null;
    return (
      <div ref={ref} style={style} className="flex items-center gap-1" data-testid="floating-chat-anon-wrap">
        <DragGripBadge handleProps={dragHandleProps} testId="floating-chat-anon-grip" />
        <button
          onClick={() => setOpen(true)}
          className="relative group inline-flex items-center gap-2 bg-[var(--ag-gold)] hover:bg-[#FFCD3C] text-white px-4 py-3 rounded-full shadow-2xl transition-all"
          data-testid="floating-chat-anon-bubble"
          aria-label="Iniciar chat"
        >
          <ChatCircleDots size={20} weight="fill" />
          <span className="hidden sm:inline text-sm font-semibold">¿Dudas? Chatea con el taller</span>
        </button>
      </div>
    );
  }

  return (
    <div ref={ref} style={style} className="w-[calc(100vw-2rem)] sm:w-[380px] h-[480px] bg-white border border-gray-200 rounded-md shadow-2xl flex flex-col" data-testid="floating-chat-anon-panel">
      <div {...dragHandleProps} className="px-3 py-2 border-b border-gray-200 flex items-center justify-between bg-[var(--ag-gold)] text-white rounded-t-md" data-testid="floating-chat-anon-dragbar">
        <DotsSixVertical size={14} weight="bold" className="opacity-70" />
        <div className="min-w-0 flex-1 ml-1">
          <div className="text-sm font-semibold truncate">{orgName || "Chat con el taller"}</div>
          {stage === "chatting" && (
            <div className={`text-[9px] uppercase tracking-wider ${wsOpen ? "text-emerald-300" : "text-blue-200"}`}>
              <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1 ${wsOpen ? "bg-emerald-300" : "bg-blue-300"}`} />
              {wsOpen ? "En vivo" : "Conectando…"}
            </div>
          )}
        </div>
        <button onClick={(e)=>{e.stopPropagation(); setOpen(false);}}
          onMouseDown={(e)=>e.stopPropagation()} onTouchStart={(e)=>e.stopPropagation()}
          className="p-1.5 hover:bg-white/10 rounded" aria-label="Cerrar" data-testid="floating-chat-close-anon">
          <X size={14} weight="bold" />
        </button>
      </div>

      {stage === "form" && (
        <form onSubmit={start} className="flex-1 p-4 space-y-3 overflow-y-auto bg-gray-50" data-testid="floating-chat-anon-form">
          <p className="text-xs text-gray-700">
            Resolvemos tus dudas <b>en directo</b> antes de que reserves. Deja
            tu nombre y un email o teléfono y nuestro equipo te contestará en
            esta ventana.
          </p>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-gray-500">Tu nombre</label>
            <Input value={name} onChange={(e)=>setName(e.target.value)} placeholder="Ej. Marta" className="rounded-sm mt-1" data-testid="anon-chat-name" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-gray-500">Email o teléfono</label>
            <Input value={contact} onChange={(e)=>setContact(e.target.value)} placeholder="marta@ejemplo.com" className="rounded-sm mt-1" data-testid="anon-chat-contact" />
          </div>
          {err && <div className="text-xs text-red-600" data-testid="anon-chat-error">{err}</div>}
          <Button type="submit" disabled={busy || !orgId} className="w-full bg-[var(--ag-gold)] hover:bg-[#FFCD3C] rounded-sm" data-testid="anon-chat-start">
            {busy ? "Conectando…" : "Iniciar chat"}
          </Button>
          <p className="text-[10px] text-gray-500">
            Al continuar, aceptas que el taller use tus datos para responderte. Sin spam.
          </p>
        </form>
      )}

      {stage === "chatting" && (
        <>
          <div ref={scrollerRef} className="flex-1 overflow-y-auto p-3 space-y-1.5 bg-gray-50" data-testid="floating-chat-anon-messages">
            {messages.length === 0 && (
              <div className="text-center text-xs text-[var(--ag-text-muted)] py-8">Sin mensajes aún. Escribe abajo.</div>
            )}
            {messages.map((m) => {
              const mine = m.sender_type === "customer";
              return (
                <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] rounded-md px-2.5 py-1.5 text-sm ${mine ? "bg-[var(--ag-gold)] text-white" : "bg-white border border-gray-200 text-[#1A1A1A]"}`}>
                    {m.message_text}
                  </div>
                </div>
              );
            })}
          </div>
          <form onSubmit={send} className="p-2 border-t border-gray-200 flex gap-1.5">
            <Input value={text} onChange={(e)=>setText(e.target.value)} placeholder="Escribe…" className="rounded-sm h-9" data-testid="anon-chat-input" />
            <Button type="submit" disabled={!text.trim()} className="bg-[var(--ag-gold)] hover:bg-[#FFCD3C] rounded-sm h-9 px-3" data-testid="anon-chat-send">
              <PaperPlaneRight size={14} weight="fill" />
            </Button>
          </form>
        </>
      )}
    </div>
  );
}
